/**
 * The small inline SVG glyphs the chat panel uses, transcribed from the mock
 * (design-reference §4.18). All are decorative (`aria-hidden`) — their buttons
 * carry the accessible label.
 */

/** The chat-bubble glyph: rounded speech bubble with a tail and three dots. */
export function ChatBubbleGlyph({ size = 22 }: { size?: number }): React.JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <rect x="1.5" y="2.5" width="13" height="9.5" rx="2.5" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5 12 L5 14.5 L8 12" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" fill="none" />
      <circle cx="5.2" cy="7.2" r="0.8" fill="currentColor" />
      <circle cx="8" cy="7.2" r="0.8" fill="currentColor" />
      <circle cx="10.8" cy="7.2" r="0.8" fill="currentColor" />
    </svg>
  );
}

/** The close cross. */
export function CloseGlyph({ size = 18 }: { size?: number }): React.JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <path d="M4 4 L12 12 M12 4 L4 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

/** A circular-arrow "new conversation" glyph. */
export function ResetGlyph({ size = 14 }: { size?: number }): React.JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M13 8a5 5 0 1 1-1.6-3.7"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        fill="none"
      />
      <path d="M13 2.2 L13 4.6 L10.6 4.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </svg>
  );
}

/** Four corners pointing outward — "expand". */
export function ExpandGlyph({ size = 16 }: { size?: number }): React.JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M6.2 2 H2 V6.2 M9.8 14 H14 V9.8 M2 9.8 V14 H6.2 M14 6.2 V2 H9.8"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/** Four corners pointing inward — "collapse", the same glyph reversed. */
export function CollapseGlyph({ size = 16 }: { size?: number }): React.JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M2 6.2 H4.6 V2 M14 9.8 H11.4 V14 M6.2 14 V11.4 H2 M9.8 2 V4.6 H14"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

/** The paper-plane send glyph. */
export function SendGlyph({ size = 15 }: { size?: number }): React.JSX.Element {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <path d="M2 8 L14 2 L11 14 L7.5 9.5 Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" fill="none" />
      <path d="M7.5 9.5 L14 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}
